import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { C } from '../constants';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import MacroIcon from '../components/MacroIcon';

export default function MarketCommentaryPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");

  useEffect(() => {
    fetch("/api/market-commentary")
      .then(r => r.json())
      .then(d => { if (d.error) setErr(d.error); else setData(d); })
      .catch(() => setErr("Failed to load commentary."))
      .finally(() => setLoading(false));
  }, []);

  const sentimentColor = (score) => {
    if (score >= 65) return "#00C805";
    if (score >= 52) return "#7FD48F";
    if (score >= 45) return "#e8c875";
    if (score >= 30) return "#FF8C42";
    return "#FF3B30";
  };
  const impactColor = (i) => i === "positive" ? "#00C805" : i === "negative" ? "#FF3B30" : "#e8c875";
  const impactIcon  = (i) => i === "positive" ? "▲" : i === "negative" ? "▼" : "→";
  const severityColor = (s) => s === "high" ? "#FF3B30" : s === "medium" ? "#e8c875" : "#7FD48F";
  const weekLabel = (w) => { if (!w) return ""; const d = new Date(w+"T12:00:00"); return d.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}); };

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:"#000", minHeight:"100vh", color:"#fff", display:"flex", flexDirection:"column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700;900&family=Rajdhani:wght@600;700;900&family=Inter:wght@300;400;500;600;700&display=swap');
        html,body{background:#000!important}
        .mc-pad{max-width:860px;margin:0 auto;padding:28px 32px}
        @media(max-width:768px){.mc-pad{padding:18px 18px 100px}.hma-bottom-nav{display:flex!important}}
        .mc-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        @media(max-width:600px){.mc-grid-2{grid-template-columns:1fr}}
        .mc-card{background:#0D0D0D;border:1px solid rgba(255,255,255,0.08);border-radius:14px;padding:18px 20px}
        .mc-shimmer{background:linear-gradient(90deg,#5a3d0e,#a07030,#e8c875,#FFE87A,#e8c875,#a07030,#5a3d0e);background-size:250% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:mcShimmer 4s linear infinite}
        @keyframes mcShimmer{0%{background-position:200% center}100%{background-position:-100% center}}
        @keyframes mcPulse{0%,100%{opacity:.3}50%{opacity:.9}}
      `}</style>

      <PageHeader active="commentary" />

      <div className="mc-pad" style={{flex:1}}>
        {/* Page title */}
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:".2em", textTransform:"uppercase", color:C.gold, marginBottom:6 }}>◆ Weekly Market Analysis</div>
          <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:"clamp(28px,4vw,40px)", fontWeight:900, color:"#fff", margin:"0 0 6px", lineHeight:1.1 }}>The Market Playbook</h1>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.45)", fontStyle:"italic", marginBottom: data ? 4 : 0 }}>Your weekly strategy, setups, and what actually matters.</div>
          {data && <div style={{ fontSize:13, color:C.hint }}>Week of {weekLabel(data.weekOf)}</div>}
        </div>

        {loading && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[180,120,200,160].map((h,i) => (
              <div key={i} style={{ height:h, borderRadius:14, background:"rgba(255,255,255,0.04)", animation:"mcPulse 1.5s ease-in-out infinite", animationDelay:`${i*0.15}s` }}/>
            ))}
          </div>
        )}

        {err && <div style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(255,59,48,0.08)", border:"1px solid rgba(255,59,48,0.2)", borderRadius:12, padding:"14px 18px", color:"#FCA5A5", fontSize:13 }}><AlertTriangle size={14} strokeWidth={2}/> {err}</div>}

        {data && !loading && (<>

          {/* Sentiment banner */}
          <div style={{ background:"linear-gradient(135deg,#0D0800,#180E00)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:16, padding:"20px 22px", marginBottom:16, position:"relative", overflow:"hidden" }}>
            <div style={{ height:4, background:"linear-gradient(90deg,#5a3d0e,#e8c875,#ffe87a,#e8c875,#5a3d0e)", backgroundSize:"200% auto", animation:"mcShimmer 4s linear infinite", position:"absolute", top:0, left:0, right:0 }}/>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:14 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:36, fontWeight:900, color:sentimentColor(data.sentimentScore), lineHeight:1 }}>{data.sentimentScore}</div>
                <div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".15em", color:C.hint, marginBottom:2 }}>Sentiment Score</div>
                  <div style={{ fontSize:14, fontWeight:700, color:sentimentColor(data.sentimentScore) }}>{data.sentimentLabel}</div>
                </div>
              </div>
              <div style={{ fontSize:14, color:C.text, fontWeight:600, lineHeight:1.5, maxWidth:480 }}>{data.headline}</div>
            </div>
            {/* Score bar */}
            <div style={{ position:"relative", height:6, background:"rgba(255,255,255,0.07)", borderRadius:3, overflow:"hidden" }}>
              <div style={{ position:"absolute", left:0, top:0, bottom:0, width:`${data.sentimentScore}%`, background:`linear-gradient(to right,#FF3B30,#e8c875,#00C805)`, borderRadius:3, transition:"width 1s ease" }}/>
              <div style={{ position:"absolute", left:`${data.sentimentScore}%`, top:-2, bottom:-2, width:3, background:"#fff", borderRadius:2, transform:"translateX(-50%)" }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:5, fontSize:11, color:"rgba(255,255,255,0.25)", letterSpacing:".06em" }}>
              <span>EXTREME FEAR</span><span>NEUTRAL</span><span>EXTREME GREED</span>
            </div>
          </div>

          {/* Bottom line */}
          <div style={{ background:"rgba(201,168,76,0.06)", border:"1px solid rgba(201,168,76,0.2)", borderRadius:12, padding:"14px 18px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:10 }}>
            <span style={{ fontSize:12, flexShrink:0, marginTop:2, color:"#e8c875" }}>◆</span>
            <div>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".15em", color:C.gold, fontWeight:700, marginBottom:5 }}>Bottom Line</div>
              <div style={{ fontSize:14, color:C.text, fontWeight:600, lineHeight:1.6 }}>{data.bottomLine}</div>
            </div>
          </div>

          {/* Markets */}
          <div className="mc-card" style={{ marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
              <div style={{ fontSize:13, fontWeight:800, color:C.text }}>Market Overview</div>
              {data.markets?.keyLevel && <div style={{ fontSize:11, color:C.hint, background:"rgba(255,255,255,0.04)", borderRadius:6, padding:"3px 10px" }}>{data.markets.keyLevel}</div>}
            </div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.9)", lineHeight:1.75, marginBottom:14 }}>{data.markets?.summary}</div>
            {data.markets?.drivers?.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {data.markets.drivers.map((d,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:13, color:"rgba(255,255,255,0.85)" }}>
                    <span style={{ color:C.gold, flexShrink:0, marginTop:1 }}>◆</span>{d}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Macro themes */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".12em", color:C.hint, marginBottom:10 }}>Macro Environment</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {(data.macro || []).map((m, i) => (
                <div key={i} className="mc-card" style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                  <span style={{ flexShrink:0, display:"flex", alignItems:"center" }}><MacroIcon theme={m.theme}/></span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:800, color:C.text }}>{m.theme}</span>
                      <span style={{ fontSize:10, fontWeight:700, color:impactColor(m.impact), background:`${impactColor(m.impact)}18`, borderRadius:20, padding:"2px 8px", letterSpacing:".04em" }}>{impactIcon(m.impact)} {m.impact}</span>
                    </div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.88)", lineHeight:1.65 }}>{m.summary}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sectors */}
          {data.sectors && (
            <div className="mc-card" style={{ marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:800, color:C.text, marginBottom:14 }}>Sector Rotation</div>
              <div className="mc-grid-2">
                <div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".14em", color:C.green, fontWeight:700, marginBottom:8 }}>▲ Leading</div>
                  {(data.sectors.leading || []).map((s,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
                      <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:800, color:C.text, flexShrink:0 }}>{s.name}</span>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.72)" }}>{s.reason}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".14em", color:C.neg, fontWeight:700, marginBottom:8 }}>▼ Lagging</div>
                  {(data.sectors.lagging || []).map((s,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
                      <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:800, color:C.text, flexShrink:0 }}>{s.name}</span>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.72)" }}>{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Risks + Opportunities */}
          <div className="mc-grid-2" style={{ marginBottom:12 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".14em", color:"rgba(255,59,48,0.8)", fontWeight:700 }}>Key Risks</div>
              {(data.risks || []).map((r,i) => (
                <div key={i} className="mc-card" style={{ borderLeft:`3px solid ${severityColor(r.severity)}`, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:4 }}>{r.title}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.78)", lineHeight:1.55 }}>{r.detail}</div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".1em", color:severityColor(r.severity), marginTop:6 }}>{r.severity} severity</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".14em", color:"rgba(0,200,5,0.8)", fontWeight:700 }}>Opportunities</div>
              {(data.opportunities || []).map((o,i) => (
                <div key={i} className="mc-card" style={{ borderLeft:`3px solid ${o.conviction==="high"?C.green:"rgba(0,200,5,0.4)"}`, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:4 }}>{o.title}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.78)", lineHeight:1.55 }}>{o.detail}</div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".1em", color:o.conviction==="high"?C.green:"rgba(0,200,5,0.55)", marginTop:6 }}>{o.conviction} conviction</div>
                </div>
              ))}
            </div>
          </div>

          {/* Week Ahead */}
          {data.weekAhead && (
            <div className="mc-card" style={{ marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:C.text, marginBottom:14 }}>Week Ahead</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.88)", lineHeight:1.7, marginBottom:14 }}>{data.weekAhead.summary}</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:16 }}>
                {data.weekAhead.keyEarnings?.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:C.hint, marginBottom:7 }}>Earnings</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {data.weekAhead.keyEarnings.map(t => (
                        <a key={t} href={`/?t=${t}`} style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:12, fontWeight:800, padding:"4px 10px", borderRadius:20, background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.25)", color:C.gold, textDecoration:"none", letterSpacing:".04em" }}>{t}</a>
                      ))}
                    </div>
                  </div>
                )}
                {data.weekAhead.keyEvents?.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:C.hint, marginBottom:7 }}>Key Events</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {data.weekAhead.keyEvents.map((ev,i) => (
                        <div key={i} style={{ fontSize:12, color:"rgba(255,255,255,0.82)", display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ width:4, height:4, borderRadius:"50%", background:C.gold, flexShrink:0, display:"inline-block" }}/>
                          {ev}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)", textAlign:"center", marginTop:8 }}>
            Generated {data.generatedAt ? new Date(data.generatedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}) : ""} · Refreshes weekly · Not financial advice
          </div>
        </>)}
      </div>
      <SiteFooter />
    </div>
  );
}
